'use client';

import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink, PrismicNextImage } from '@prismicio/next';
import { useCountUp } from '@/hooks';

export type FeatureHighlightsProps =
  SliceComponentProps<Content.FeatureHighlightsSlice>;

/**
 * Animated 0 → target counter. Honors prefers-reduced-motion.
 */
const IntroStatCounter: FC<{
  target: number;
  suffix: string;
  className?: string;
}> = ({ target, suffix, className }) => {
  // M-05: render the real value in markup; count up only as enhancement.
  const { display, ref } = useCountUp<HTMLSpanElement>(target);

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
};

const StarRating: FC<{ count: number | null }> = ({ count }) => {
  const filled = Math.min(Math.max(Math.round(count ?? 0), 0), 5);
  return (
    <div className="flex gap-[3px]">
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8,1 L9.65,5.73 L14.66,5.84 L10.66,8.86 L12.11,13.66 L8,10.8 L3.89,13.66 L5.34,8.86 L1.34,5.84 L6.35,5.73 Z"
            fill={i < filled ? '#F59E0B' : '#D1D5DB'}
          />
        </svg>
      ))}
    </div>
  );
};

const FeatureHighlights: FC<FeatureHighlightsProps> = ({ slice }) => {
  const { eyebrow, title, description, cta_button } = slice.primary;
  const buttonText = isFilled.link(cta_button)
    ? (cta_button as { text?: string } & typeof cta_button).text
    : undefined;
  const introStats =
    slice.variation === 'intro_left_features_grid_right'
      ? ((
          slice.primary as unknown as {
            intro_stats?: {
              value?: number | null;
              suffix?: string;
              label?: string;
            }[];
          }
        ).intro_stats ?? [])
      : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative px-5 py-[80px] md:px-8 lg:px-[60px]"
    >
      <div className="mx-auto w-full max-w-[1140px]">
        {/* Eyebrow row */}
        <div className="flex items-center gap-[60px] mb-[40px]">
          {isFilled.keyText(eyebrow) && (
            <div className="border border-secondary-vibrant-purple px-4 py-2 rounded-full shrink-0">
              <span className="text-[14px] uppercase text-[#041118] leading-[1.2] font-normal tracking-wider">
                {eyebrow}
              </span>
            </div>
          )}
          <div className="flex-1 h-px bg-[#ccc]" />
        </div>

        {/* Main content */}
        <div className="flex flex-col lg:flex-row gap-[80px] items-start">
          {/* Left: sticky intro block */}
          <div className="lg:sticky lg:top-[100px] flex flex-col gap-6 lg:w-[540px] shrink-0">
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[42px] font-semibold leading-[1.4] text-[#041118]">
                    {children}
                  </h2>
                ),
              }}
            />
            <div className="text-[16px] font-light leading-[1.5] text-[#041118]">
              <PrismicRichText field={description} />
            </div>
            {isFilled.link(cta_button) && (
              <PrismicNextLink
                field={cta_button}
                className="primary-button self-start whitespace-nowrap"
              >
                {buttonText || 'View open positions'}
              </PrismicNextLink>
            )}

            {introStats.length > 0 && (
              <div
                className="relative mt-4 grid w-full gap-y-6"
                style={{
                  gridTemplateColumns: `repeat(${introStats.length}, minmax(0, 1fr))`,
                }}
              >
                {introStats.map((stat, i) => (
                  <div
                    key={`intro-stat-${i}`}
                    className={`relative flex flex-col items-start gap-3 px-4 py-2 md:px-6 border-r border-solid border-[#cccccc] ${
                      i === 0 ? 'border-l' : ''
                    }`}
                  >
                    {/* purple glow halo behind the number */}
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-x-2 -top-2 h-[80px] rounded-full blur-2xl"
                      style={{
                        background:
                          'radial-gradient(50% 60% at 50% 50%, rgba(168,135,255,0.45) 0%, rgba(168,135,255,0) 70%)',
                      }}
                    />
                    <div className="relative leading-none text-[#4B0FEA]">
                      <IntroStatCounter
                        target={Number(stat.value ?? 0)}
                        suffix=""
                        className="text-[60px] font-medium leading-[1] md:text-[64px]"
                      />
                      <span className="text-[28px] font-medium leading-[1] md:text-[32px]">
                        {stat.suffix || '%'}
                      </span>
                    </div>
                    {stat.label && (
                      <p className="relative max-w-[140px] text-[14px] font-bold leading-[1.3] text-[#041118] md:text-[15px]">
                        {stat.label}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: cards */}
          <div className="flex flex-col gap-6 lg:w-[460px] shrink-0 w-full">
            {slice.variation === 'withReviews'
              ? slice.primary.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-6 p-9 rounded-[24px] border border-[#ccc] backdrop-blur-[21px]"
                  >
                    {isFilled.image(feature.profile_photo) && (
                      <div className="size-[148px] rounded-full overflow-hidden shrink-0 relative">
                        <PrismicNextImage
                          field={feature.profile_photo}
                          className="object-cover size-full"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <StarRating count={feature.stars} />
                      {isFilled.keyText(feature.review) && (
                        <p className="text-[16px] font-light leading-[1.5] text-[#041118]">
                          {feature.review}
                        </p>
                      )}
                      <div className="text-[24px] font-semibold leading-[1.2] text-[#041118]">
                        <PrismicRichText
                          field={feature.name}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="leading-[1.2]">{children}</p>
                            ),
                          }}
                        />
                        <PrismicRichText
                          field={feature.role}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="leading-[1.2]">{children}</p>
                            ),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              : slice.primary.features.map((feature, index) => {
                  const cardTag = (feature as { card_tag?: string }).card_tag;
                  const hoverImage = (
                    feature as {
                      hover_image?: import('@prismicio/client').ImageField;
                    }
                  ).hover_image;
                  const hasHover = isFilled.image(hoverImage);
                  return (
                    <div
                      key={index}
                      className="group/card relative flex flex-col gap-6 overflow-hidden rounded-[24px] border border-[#ccc] p-9 backdrop-blur-[21px] transition-colors duration-500"
                    >
                      {hasHover && (
                        <>
                          <PrismicNextImage
                            field={hoverImage}
                            fill
                            className="pointer-events-none absolute inset-0 size-full object-cover opacity-0 transition-opacity duration-500 group-hover/card:opacity-100"
                            alt=""
                          />
                          <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#041118]/85 to-[#041118]/80 opacity-0 transition-opacity duration-500 group-hover/card:opacity-100"
                          />
                        </>
                      )}

                      <div className="relative z-[1] flex flex-col gap-6">
                        {isFilled.image(feature.icon) && (
                          <div className="size-[51px] shrink-0 overflow-hidden relative">
                            <PrismicNextImage
                              field={feature.icon}
                              className="object-contain size-full"
                            />
                          </div>
                        )}
                        <div className="flex flex-col gap-3">
                          {cardTag && (
                            <div
                              className={`flex items-center gap-2 text-[12px] font-semibold uppercase leading-[1.3] tracking-[0.08em] text-[#4B0FEA] transition-colors duration-500 md:text-[13px] ${hasHover ? 'group-hover/card:text-[#00f557]' : ''}`}
                            >
                              <span
                                aria-hidden="true"
                                className={`inline-block size-[8px] shrink-0 rounded-full border-[1.5px] border-[#4B0FEA] transition-colors duration-500 ${hasHover ? 'group-hover/card:border-[#00f557]' : ''}`}
                              />
                              {cardTag}
                            </div>
                          )}
                          {isFilled.keyText(feature.title) && (
                            <h3
                              className={`text-[24px] font-semibold leading-[1.2] text-[#041118] transition-colors duration-500 ${hasHover ? 'group-hover/card:text-white' : ''}`}
                            >
                              {feature.title}
                            </h3>
                          )}
                          <div
                            className={`text-[16px] font-light leading-[1.5] text-[#041118] transition-colors duration-500 ${hasHover ? 'group-hover/card:text-white' : ''}`}
                          >
                            <PrismicRichText
                              field={feature.description}
                              components={{
                                paragraph: ({ children }) => (
                                  <p className="m-0 leading-[1.5] [&+ul]:mt-2 [&+ol]:mt-2">
                                    {children}
                                  </p>
                                ),
                                list: ({ children }) => (
                                  <ul className="m-0 list-disc space-y-1 pl-5">
                                    {children}
                                  </ul>
                                ),
                                oList: ({ children }) => (
                                  <ol className="m-0 list-decimal space-y-1 pl-5">
                                    {children}
                                  </ol>
                                ),
                                listItem: ({ children }) => (
                                  <li className="leading-[1.5]">{children}</li>
                                ),
                                oListItem: ({ children }) => (
                                  <li className="leading-[1.5]">{children}</li>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureHighlights;
